import React, { useState, useEffect } from 'react';
// Context
import { useCTALogsData } from '../../../../context/CTALogsContext';
import { useCTALogClassesData } from '../../../../context/CTALogClassesContext';
import { useCTALogTypesData } from '../../../../context/CTALogTypesContext';
// Modules
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
// Components
import Loading from '../../../../components/loading/Loading';
import { toastSuccess, toastError } from '../../../../components/feedback/Feedback';
// Form
import useInput from '../../../../components/form/hooks/useInput';
import DefaultInput from '../../../../components/form/inputs/DefaultInput';
import DefaultSelect from '../../../../components/form/inputs/DefaultSelect';
import { isNotEmpty, isNotNullOrUndefinedOrEmpty } from '../../../../components/form/validator/ValidatorRules';
import { isMandatoryMessage } from '../../../../components/form/validator/ValidatorMessages';
// Utils
import { CTALogStatusOptions } from '../../../../utils/Enums';
import { isUndefinedOrNull } from '../../../../utils/Utils';
// Actions
import { save as CTALogSave } from '../../../../actions/ctalogs';

export default function CTALogForm({ CTALog, handleCloseModal }) {
  // Form
  const {
    value: nameValue,
    isValid: nameIsValid,
    hasError: nameHasError,
    valueChangeHandler: nameChangeHandler,
    inputBlurHandler: nameBlurHandler,
  } = useInput(isNotEmpty);
  const {
    value: classValue,
    isValid: classIsValid,
    hasError: classHasError,
    valueChangeHandler: classChangeHandler,
    inputBlurHandler: classBlurHandler,
  } = useInput(isNotNullOrUndefinedOrEmpty);
  const {
    value: typeValue,
    isValid: typeIsValid,
    hasError: typeHasError,
    valueChangeHandler: typeChangeHandler,
    inputBlurHandler: typeBlurHandler,
  } = useInput(isNotNullOrUndefinedOrEmpty);
  const {
    value: statusValue,
    isValid: statusIsValid,
    hasError: statusHasError,
    valueChangeHandler: statusChangeHandler,
    inputBlurHandler: statusBlurHandler,
  } = useInput(isNotNullOrUndefinedOrEmpty);
  const {
    value: obsValue,
    valueChangeHandler: obsChangeHandler,
    inputBlurHandler: obsBlurHandler,
  } = useInput(() => {});

  let formIsValid = false;

  if (nameIsValid && classIsValid && typeIsValid && statusIsValid) {
    formIsValid = true;
  }

  // Context
  const { state, dispatch } = useCTALogsData();
  const { state: classesState } = useCTALogClassesData();
  const { state: typesState } = useCTALogTypesData();

  // State
  const [isLoading, setIsLoading] = useState(false);

  // useEffect
  useEffect(
    () => {
      if (CTALog) {
        nameChangeHandler(CTALog.name);
        classChangeHandler(CTALog.classInfo);
        typeChangeHandler(CTALog.typeInfo);
        const defaultCTALogStatusOption = CTALogStatusOptions.find((option) => option.id === CTALog.statusId);
        statusChangeHandler(defaultCTALogStatusOption);
        obsChangeHandler(CTALog.obs);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Methods
  const validateAllFormInputs = () => {
    nameBlurHandler();
    nameChangeHandler(nameValue);
    classBlurHandler();
    classChangeHandler(classValue);
    typeBlurHandler();
    typeChangeHandler(typeValue);
    statusBlurHandler();
    statusChangeHandler(statusValue);
  };

  const saveCTALog = async () => {
    setIsLoading(true);

    await CTALogSave({
      data: {
        id: CTALog && !isUndefinedOrNull(CTALog.id) ? CTALog.id : 0,
        name: nameValue,
        classId: classValue.id,
        typeId: typeValue.id,
        statusId: statusValue.id,
        obs: obsValue,
      },
      success: (data) => {
        toastSuccess("A lista de CTA's log foi atualizada com sucesso");
        setIsLoading(false);
        handleCloseModal();
        dispatch({
          type: 'SET_RELOAD',
          payload: !state.reload,
        });
      },
      error: (error) => {
        toastError('Ocorreu um erro ao submeter o CTA');
        setIsLoading(false);
      },
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formIsValid) {
      validateAllFormInputs();
      return;
    }

    saveCTALog();
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col>
            <DefaultInput
              label={'Nome'}
              name={'name'}
              required={true}
              type={'text'}
              value={nameValue}
              onChange={nameChangeHandler}
              onBlur={nameBlurHandler}
              hasError={nameHasError}
              errorMessage={isMandatoryMessage}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <DefaultSelect
              label={'Classe'}
              instanceId={'classSelect'}
              required={true}
              options={classesState.data}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              defaultValue={null}
              value={classValue}
              onChange={classChangeHandler}
              onBlur={classBlurHandler}
              hasError={classHasError}
              errorMessage={isMandatoryMessage}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <DefaultSelect
              label={'Tipo'}
              instanceId={'typeSelect'}
              required={true}
              options={typesState.data}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              defaultValue={null}
              value={typeValue}
              onChange={typeChangeHandler}
              onBlur={typeBlurHandler}
              hasError={typeHasError}
              errorMessage={isMandatoryMessage}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <DefaultSelect
              label={'Estado'}
              instanceId={'statusSelect'}
              required={true}
              options={CTALogStatusOptions}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              defaultValue={null}
              value={statusValue}
              onChange={statusChangeHandler}
              onBlur={statusBlurHandler}
              hasError={statusHasError}
              errorMessage={isMandatoryMessage}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <DefaultInput
              label={'Observações'}
              name={'obs'}
              required={true}
              type={'textarea'}
              value={obsValue}
              onChange={obsChangeHandler}
              onBlur={obsBlurHandler}
            />
          </Col>
        </Row>
        <Row>
          <Col className="text-end">
            <Button type={'submit'}>Submeter</Button>
          </Col>
        </Row>
      </Form>
      {isLoading && <Loading label={'A carregar...'} />}
    </>
  );
}
